import { apiRequest } from '../../shared/api/client';

/**
 * Purchase orders — mirrors Invetrak.Api.Endpoints.PurchaseOrderEndpoints.
 *   GET    /api/v1/purchase-orders                        po:read
 *   GET    /api/v1/purchase-orders/{id}                   po:read
 *   POST   /api/v1/purchase-orders                        po:write
 *   PUT    /api/v1/purchase-orders/{id}                   po:write
 *   POST   /api/v1/purchase-orders/{id}/status            po:write
 *   DELETE /api/v1/purchase-orders/{id}                   po:write
 *   GET    /api/v1/purchase-orders/{id}/lines             po:read
 *   POST   /api/v1/purchase-orders/{id}/lines             po:write
 *   PUT    /api/v1/purchase-orders/{id}/lines/{lineId}    po:write
 *   DELETE /api/v1/purchase-orders/{id}/lines/{lineId}    po:write
 *   /api/v1/purchase-order-ship-tos                       po:read / po:write
 *
 * Two things are never in a request body, because the server owns them:
 *   - the PO number, allocated from the PURCHASE_ORDER sequence at creation;
 *   - the totals and the received part of the status, derived from the lines.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

/**
 * Six values, and only four of them are anybody's decision.
 *
 * `partially_received` and `received` are observations the server makes about the lines, so
 * nothing here sets them — see the status endpoint, which takes an ACTION rather than a status.
 */
export type PurchaseOrderStatus =
  | 'open'
  | 'issued'
  | 'partially_received'
  | 'received'
  | 'closed'
  | 'cancelled';

/** The explicit acts. Everything else about a purchase order's progress is derived. */
export type PurchaseOrderAction = 'issue' | 'close' | 'cancel' | 'reopen';

/** Anything still expecting goods — what the list opens on. */
export const OPEN_STATUSES: PurchaseOrderStatus[] = ['open', 'issued', 'partially_received'];

export interface PurchaseOrderDto {
  purchaseOrderId: string;
  poNumber: string;
  warehouseId: string;
  warehouseCode: string;
  supplierId: string;
  supplierNumber: string;
  supplierName: string;
  orderDateUtc: string;
  expectedReceiptDateUtc: string | null;
  status: PurchaseOrderStatus;
  shipMethodId: string | null;
  shipMethodCode: string | null;
  paymentTermId: string | null;
  paymentTermCode: string | null;
  shipToId: string | null;
  /**
   * The address as it stood when the order was placed, not a live read of the ship-to book.
   * Correcting the book must not rewrite where an order already sent was sent.
   */
  shipToName: string | null;
  shipToAddressLine1: string | null;
  shipToAddressLine2: string | null;
  shipToCity: string | null;
  shipToStateProvince: string | null;
  shipToPostalCode: string | null;
  shipToCountryCode: string | null;
  shipToPhone: string | null;
  shipToFax: string | null;
  attentionTo: string | null;
  placedBy: string | null;
  notes: string | null;
  externalPoNumber: string | null;
  subtotalAmount: number;
  taxAmount: number;
  totalAmount: number;
  currencyCode: string;
  createdAtUtc: string;
  lastModifiedUtc: string;
  lineCount: number;
  /** Whether anything has arrived — what decides if the order can still be cancelled or deleted. */
  hasReceipts: boolean;
}

export interface PurchaseOrderLineDto {
  purchaseOrderLineId: string;
  purchaseOrderId: string;
  /** Assigned server-side, and never renumbered: the supplier is holding a piece of paper. */
  lineNumber: number;
  itemId: string;
  itemNumber: string;
  /** The description as it read when the line was written, not the item's current one. */
  itemDescription: string | null;
  quantityOrdered: number;
  quantityReceived: number;
  uomId: string;
  uomCode: string;
  unitPrice: number;
  lineTotal: number;
  dateRequiredByUtc: string | null;
  status: string;
}

export interface ShipToDto {
  purchaseOrderShipToId: string;
  shipToName: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateProvince: string | null;
  postalCode: string | null;
  countryCode: string | null;
  phone: string | null;
  fax: string | null;
  isActive: boolean;
  createdAtUtc: string;
  /** On at least one order, so it can only be deactivated, not deleted. */
  isInUse: boolean;
}

export interface CreatePurchaseOrderRequest {
  warehouseId: string;
  supplierId: string;
  expectedReceiptDateUtc?: string | null;
  paymentTermId?: string | null;
  shipToId?: string | null;
  attentionTo?: string | null;
  placedBy?: string | null;
  notes?: string | null;
  externalPoNumber?: string | null;
}

/** No number, warehouse or supplier — see the endpoint's remarks for why those are fixed. */
export interface UpdatePurchaseOrderRequest {
  expectedReceiptDateUtc?: string | null;
  paymentTermId?: string | null;
  shipToId?: string | null;
  attentionTo?: string | null;
  placedBy?: string | null;
  notes?: string | null;
  externalPoNumber?: string | null;
  taxAmount?: number | null;
}

export interface SavePurchaseOrderLineRequest {
  itemId: string;
  quantityOrdered: number;
  uomId: string;
  unitPrice?: number;
  dateRequiredByUtc?: string | null;
}

export interface SaveShipToRequest {
  shipToName: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  phone?: string | null;
  fax?: string | null;
  isActive?: boolean;
}

export interface PurchaseOrderQuery {
  warehouseId?: string;
  supplierId?: string;
  status?: string;
  openOnly?: boolean;
}

export async function listPurchaseOrders(
  query: PurchaseOrderQuery = {},
  signal?: AbortSignal,
): Promise<PurchaseOrderDto[]> {
  const params = new URLSearchParams();
  if (query.warehouseId) params.set('warehouseId', query.warehouseId);
  if (query.supplierId) params.set('supplierId', query.supplierId);
  if (query.status) params.set('status', query.status);
  if (query.openOnly) params.set('openOnly', 'true');

  const qs = params.toString();
  const { data } = await apiRequest<PurchaseOrderDto[]>(
    `/v1/purchase-orders/${qs ? `?${qs}` : ''}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function getPurchaseOrder(id: string, signal?: AbortSignal): Promise<PurchaseOrderDto> {
  const { data } = await apiRequest<PurchaseOrderDto>(
    `/v1/purchase-orders/${encodeURIComponent(id)}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function createPurchaseOrder(
  body: CreatePurchaseOrderRequest,
  signal?: AbortSignal,
): Promise<PurchaseOrderDto> {
  const { data } = await apiRequest<PurchaseOrderDto>('/v1/purchase-orders/', {
    method: 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updatePurchaseOrder(
  id: string,
  body: UpdatePurchaseOrderRequest,
  signal?: AbortSignal,
): Promise<PurchaseOrderDto> {
  const { data } = await apiRequest<PurchaseOrderDto>(`/v1/purchase-orders/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/**
 * An action, not a status. Issuing, closing and cancelling are three different things with three
 * different rules, and a settable field could not express "cancel is refused once goods arrived".
 */
export async function setPurchaseOrderStatus(
  id: string,
  action: PurchaseOrderAction,
  signal?: AbortSignal,
): Promise<PurchaseOrderDto> {
  const { data } = await apiRequest<PurchaseOrderDto>(
    `/v1/purchase-orders/${encodeURIComponent(id)}/status`,
    { method: 'POST', body: { action }, signal, ...COOKIE_INIT },
  );
  return data;
}

export async function deletePurchaseOrder(id: string, signal?: AbortSignal): Promise<void> {
  await apiRequest<void>(`/v1/purchase-orders/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    signal,
    ...COOKIE_INIT,
  });
}

export async function listPurchaseOrderLines(
  id: string,
  signal?: AbortSignal,
): Promise<PurchaseOrderLineDto[]> {
  const { data } = await apiRequest<PurchaseOrderLineDto[]>(
    `/v1/purchase-orders/${encodeURIComponent(id)}/lines`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Every line write returns the WHOLE set, because a write recalculates each line's status — so the
 * grid is replaced rather than patched.
 */
export async function savePurchaseOrderLine(
  id: string,
  lineId: string | null,
  body: SavePurchaseOrderLineRequest,
  signal?: AbortSignal,
): Promise<PurchaseOrderLineDto[]> {
  const path = lineId
    ? `/v1/purchase-orders/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`
    : `/v1/purchase-orders/${encodeURIComponent(id)}/lines`;

  const { data } = await apiRequest<PurchaseOrderLineDto[]>(path, {
    method: lineId ? 'PUT' : 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function deletePurchaseOrderLine(
  id: string,
  lineId: string,
  signal?: AbortSignal,
): Promise<PurchaseOrderLineDto[]> {
  const { data } = await apiRequest<PurchaseOrderLineDto[]>(
    `/v1/purchase-orders/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`,
    { method: 'DELETE', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function listShipTos(
  includeInactive = false,
  signal?: AbortSignal,
): Promise<ShipToDto[]> {
  const { data } = await apiRequest<ShipToDto[]>(
    `/v1/purchase-order-ship-tos/${includeInactive ? '?includeInactive=true' : ''}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function createShipTo(body: SaveShipToRequest, signal?: AbortSignal): Promise<ShipToDto> {
  const { data } = await apiRequest<ShipToDto>('/v1/purchase-order-ship-tos/', {
    method: 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updateShipTo(
  id: string,
  body: SaveShipToRequest,
  signal?: AbortSignal,
): Promise<ShipToDto> {
  const { data } = await apiRequest<ShipToDto>(
    `/v1/purchase-order-ship-tos/${encodeURIComponent(id)}`,
    { method: 'PUT', body, signal, ...COOKIE_INIT },
  );
  return data;
}

export async function deleteShipTo(id: string, signal?: AbortSignal): Promise<void> {
  await apiRequest<void>(`/v1/purchase-order-ship-tos/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    signal,
    ...COOKIE_INIT,
  });
}

/** One line to describe a ship-to in a picker or a summary. */
export function shipToSummary(s: ShipToDto): string {
  const parts = [s.addressLine1, s.city, s.stateProvince].filter(Boolean);
  return parts.length > 0 ? `${s.shipToName} — ${parts.join(', ')}` : s.shipToName;
}
